import { useEffect, useState } from "react";
import { cn } from "../../utils/cn";
import { Button } from "../base";
import { motion } from "framer-motion";

export function Header({ logo, links, buttons, className, ...rest }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const headerStyle = scrolled
        ? "backdrop-blur-md bg-[#e8f9fd]/50 shadow-sm"
        : "bg-[#e8f9fd]";
    const linkClass =
        "text-sm font-normal text-base-600 dark:text-base-400 hover:text-cyan-500 dark:hover:text-cyan-400 transition-colors";
    const mobileLinkClass =
        "block text-sm font-normal text-base-600 dark:text-base-400 hover:bg-cyan-100 hover:text-cyan-500 py-3 px-4 rounded-lg transition-colors";

    return (
        <header
            className={cn(
                "sticky top-0 w-full z-20 transition-all duration-300 ease-out",
                headerStyle,
            )}
        >
            <motion.nav
                initial={{ opacity: 0, y: -16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className={cn(
                    "relative h-16 container max-w-6xl px-4 sm:px-6 lg:px-8 mx-auto flex justify-between items-center gap-4 lg:gap-8 z-10",
                    className,
                )}
                {...rest}
            >
                <a href={logo.href} className="shrink-0">
                    <img
                        src={logo.src}
                        alt={logo.alt}
                        className="h-10 w-auto dark:invert"
                    />
                </a>

                <ul className="hidden md:flex md:absolute md:left-1/2 md:-translate-x-1/2 md:items-center md:flex-row md:gap-8">
                    {links.map((link, index) => (
                        <motion.li
                            key={index}
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                                duration: 0.4,
                                delay: 0.1 + index * 0.07,
                            }}
                        >
                            <a href={link.href} className={linkClass}>
                                {link.label}
                            </a>
                        </motion.li>
                    ))}
                </ul>

                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: 0.4 }}
                    className="flex items-center gap-2 shrink-0"
                >
                    <div className="hidden md:flex gap-2">
                        {buttons.map((button, index) => (
                            <Button key={index} {...button} />
                        ))}
                    </div>
                    <Button
                        icon={open ? "tabler:x" : "tabler:menu-2"}
                        color="transparent"
                        className="p-2 md:hidden"
                        onClick={() => setOpen(!open)}
                    />
                </motion.div>
            </motion.nav>

            {open && (
                <div className="md:hidden absolute top-16 right-4 w-2/3 border border-base-200 dark:border-base-800 rounded-xl overflow-hidden bg-[#e8f9fd] dark:bg-base-900 shadow-xl z-10">
                    <ul className="flex flex-col p-2">
                        {links.map((link, index) => (
                            <li key={index}>
                                <a
                                    href={link.href}
                                    className={mobileLinkClass}
                                    onClick={() => setOpen(false)}
                                >
                                    {link.label}
                                </a>
                            </li>
                        ))}
                        <li className="border-t border-base-200 dark:border-base-800 mt-2 pt-2 px-2 pb-1 flex flex-col gap-2">
                            {buttons.map((button, index) => (
                                <Button
                                    key={index}
                                    {...button}
                                    className="w-full justify-center"
                                />
                            ))}
                        </li>
                    </ul>
                </div>
            )}
        </header>
    );
}
